import { createFileRoute, useNavigate } from '@tanstack/react-router'

import { PROJECTS } from '@brickup/shared/locales/de/projects'
import { usePublicDefaultAdvisor } from '@/api/advisors'
import {
  AdvisorCtaSection,
  PublicFooter,
  PublicTopBar,
} from '@/components/Public/Brand'
import {
  parseProjectsSearch,
  readProjectsSearch,
  writeProjectsSearch,
  type ProjectFilters,
} from '@/components/Public/Projects/Filter/filter-state'
import type { ProjectSort } from '@/components/Public/Projects/project-sort'
import { PublicProjectsList } from '@/components/Public/Projects/Public/PublicProjectsList'
import { PAGE_BODY_CLASS } from '@/constants/page-column'
import { bookableAdvisorName } from '@/utils/advisor-labels'

/** Every published object, as one column a visitor reads down. */
function PublicProjects() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { filters, sort } = readProjectsSearch(search)

  // No project of its own to take a contact person from, so the whole page is
  // the configured default advisor's — in the bar and again at the foot.
  const { data: advisor } = usePublicDefaultAdvisor()

  // The address carries the whole narrowing, so a link to it reopens what the
  // sender was looking at and the browser's back button undoes one step of it.
  // Pushed rather than replaced: every change that reaches here is one
  // deliberate act — a bound commits when its field is left, not per keystroke.
  const go = (nextFilters: ProjectFilters, nextSort: ProjectSort) =>
    navigate({ search: writeProjectsSearch(nextFilters, nextSort) })

  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-white">
      <PublicTopBar advisor={advisor} />

      <div className={PAGE_BODY_CLASS}>
        <PublicProjectsList
          filters={filters}
          onFiltersChange={next => go(next, sort)}
          sort={sort}
          onSortChange={next => go(filters, next)}
        />
      </div>

      <AdvisorCtaSection
        advisor={advisor}
        eyebrow={PROJECTS.publicOverviewCta.eyebrow}
        headline={PROJECTS.publicOverviewCta.headline}
        body={PROJECTS.publicOverviewCta.body(bookableAdvisorName(advisor))}
      />

      <PublicFooter />
    </div>
  )
}

export const Route = createFileRoute('/public/projects/')({
  validateSearch: parseProjectsSearch,
  component: PublicProjects,
})
